import React, { useState } from "react";
import PropTypes from "prop-types";
import { fieldClass, labelClass, primaryButton, secondaryButton } from "../template/templateLabels";

// What a worker did at a kiosk (POST /api/service/visits): the A4 sheets in its
// cassette when they leave, and each tank's mark before and after they topped it up.
// Everything is optional but something is done. The marks count from the tank's
// lowest line, 0, to full. Of the colour tanks the lowest one is recorded: it runs
// out first. An admin's order for the kiosk goes once visits have counted the paper
// and filled the tanks it is for.

const EMPTY_TANK = { before: null, after: null };

// The marks of one tank, as buttons; pressing the chosen one again clears it. The
// printer's edit form has them too (printer/PrinterForm.js), where six fit a phone.
export const MarkPicker = ({ marks, value, onChange }) => (
	<div className="flex flex-wrap gap-2">
		{Array.from({ length: marks + 1 }, (_, mark) => (
			<button
				key={mark}
				type="button"
				onClick={() => onChange(value === mark ? null : mark)}
				className={
					"w-11 h-11 text-base font-semibold rounded-lg border focus:outline-none " +
					(value === mark
						? "text-white bg-purple-600 border-purple-600"
						: "text-gray-700 bg-white border-gray-300 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600")
				}
			>
				{mark}
			</button>
		))}
	</div>
);

const TankFields = ({ title, note, marks, estimate, value, onChange }) => (
	<div className="mt-5">
		<p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</p>
		<p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
			{note}
			{estimate ? ` Тооцоогоор одоо ≈ ${estimate.level} зураас.` : " Энэ киоскт анх удаа бүртгэж байна."}
		</p>
		<p className={labelClass}>Нэмэхээс өмнө</p>
		<MarkPicker marks={marks} value={value.before} onChange={(before) => onChange({ ...value, before })} />
		<p className={labelClass + " mt-3"}>Нэмсний дараа</p>
		<MarkPicker marks={marks} value={value.after} onChange={(after) => onChange({ ...value, after })} />
	</div>
);

const tankError = (tank) =>
	tank.before !== null && tank.after !== null && tank.after < tank.before
		? "Нэмсний дараах түвшин нэмэхээс өмнөхөөс бага байж болохгүй"
		: null;

const VisitForm = ({ kiosk, marks, order, busy, onSave, onClose }) => {
	const [paper, setPaper] = useState("");
	const [black, setBlack] = useState(EMPTY_TANK);
	const [color, setColor] = useState(EMPTY_TANK);

	const touched = (tank) => tank.before !== null || tank.after !== null;
	const error = tankError(black) || tankError(color);
	const paperValue = paper.trim();
	const paperBad = paperValue !== "" && !/^\d{1,4}$/.test(paperValue);
	const empty = paperValue === "" && !touched(black) && !touched(color);

	const save = () => {
		if (busy || error || paperBad || empty) return;
		onSave({
			printerId: kiosk.id,
			paper: paperValue === "" ? undefined : Number(paperValue),
			black: touched(black) ? black : undefined,
			color: touched(color) ? color : undefined,
		});
	};

	return (
		<div className="fixed inset-0 z-30 flex items-end justify-center bg-black bg-opacity-50 sm:items-center">
			<div className="w-full max-h-full px-6 py-5 overflow-y-auto bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:max-w-lg">
				<div className="flex items-start justify-between">
					<div>
						<p className="text-lg font-semibold text-gray-700 dark:text-gray-200">{kiosk.name}</p>
						<p className="text-xs text-gray-500 dark:text-gray-400">{kiosk.location} · Юу хийснээ бүртгэнэ үү</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="ml-4 text-gray-500 hover:text-gray-700 dark:text-gray-400"
						aria-label="Хаах"
					>
						<i className="fas fa-times" />
					</button>
				</div>

				{order && (
					<p className="p-2 mt-4 text-xs text-purple-700 rounded-lg bg-purple-50 dark:bg-gray-700 dark:text-purple-200">
						📌 Тушаал: {order}. Цаасны тоо, бэхний «Нэмсний дараа» түвшинг бүртгэсэн нь тушаалаас хасагдана.
					</p>
				)}

				<div className="mt-5">
					<p className="text-sm font-semibold text-gray-700 dark:text-gray-200">A4 цаас</p>
					<p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
						Цаас нэмсэн бол кассетанд одоо хэдэн хуудас байгааг бичнэ. Бүртгэлд: {kiosk.paper.sheets}.
					</p>
					<input
						className={fieldClass}
						inputMode="numeric"
						placeholder="Жишээ нь 250"
						value={paper}
						onChange={(e) => setPaper(e.target.value)}
					/>
					{paperBad && <p className="mt-1 text-xs text-red-600">Хуудасны тоог тоогоор бичнэ үү</p>}
				</div>

				<TankFields
					title="Хар бэх"
					note={`Танкны зураасыг доод зураасаас (0) дүүрэн (${marks}) хүртэл тоолно.`}
					marks={marks}
					estimate={kiosk.black}
					value={black}
					onChange={setBlack}
				/>
				<TankFields
					title="Өнгөт бэх"
					note="Гурван өнгөт танкаас хамгийн доогуурынх нь."
					marks={marks}
					estimate={kiosk.color}
					value={color}
					onChange={setColor}
				/>

				{error && <p className="mt-3 text-sm text-red-600">{error}</p>}

				<div className="flex justify-end gap-3 mt-6">
					<button type="button" className={secondaryButton} onClick={onClose}>
						Болих
					</button>
					<button
						type="button"
						className={primaryButton + (busy || error || paperBad || empty ? " opacity-50 cursor-not-allowed" : "")}
						disabled={busy || Boolean(error) || paperBad || empty}
						onClick={save}
					>
						{busy ? "Хадгалж байна…" : "Хадгалах"}
					</button>
				</div>
			</div>
		</div>
	);
};

VisitForm.propTypes = {
	kiosk: PropTypes.object.isRequired,
	marks: PropTypes.number.isRequired,
	order: PropTypes.string,
	busy: PropTypes.bool,
	onSave: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default VisitForm;
